import { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../API';

/**
 * useNotes — load + save per-user per-item free-text notes.
 *
 * Storage endpoint:
 *   GET/PUT projects/:folderId/items/:itemId/annotation/notes
 *
 * This is intentionally decoupled from per-UI annotation hooks so notes can be
 * shared across all annotation UI modules without threading through each save path.
 */
export function useNotes({
    folderId,
    itemId,
    enabled = true,
    maxLength = 5000,
}) {
    const [notes, setNotes] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [notesUpdated, setNotesUpdated] = useState(null);

    const saveTimerRef = useRef(null);
    const lastSavedValueRef = useRef('');

    const endpoint =
        folderId && itemId
            ? `projects/${folderId}/items/${itemId}/annotation/notes`
            : null;

    // Load on item change. Each effect run owns its own `cancelled` flag, so under
    // React.StrictMode (dev) the cleanup of the first invocation discards the first
    // GET and the second invocation fires a fresh GET that wins. In production the
    // effect only runs once per (folderId, itemId) change. We deliberately do NOT
    // dedupe by a "lastLoadedKey" ref here: doing so before awaiting the request
    // caused the first GET to be cancelled and the re-mount to short-circuit, which
    // pinned `loading` to true on the first item after a hard refresh.
    useEffect(() => {
        if (!enabled || !endpoint) {
            setNotes('');
            setLoading(false);
            setSaving(false);
            setError(null);
            setNotesUpdated(null);
            lastSavedValueRef.current = '';
            return undefined;
        }

        let cancelled = false;
        setLoading(true);
        setError(null);
        api.get(endpoint, {}).then((res) => {
            if (cancelled) return;
            if (res.status === 200) {
                const next = typeof res.data?.notes === 'string' ? res.data.notes : '';
                setNotes(next);
                lastSavedValueRef.current = next;
                setNotesUpdated(res.data?.notesUpdated ?? null);
            } else {
                setError(res.data?.message || 'Failed to load notes.');
            }
        }).catch((e) => {
            if (cancelled) return;
            setError(e?.message || 'Failed to load notes.');
        }).finally(() => {
            if (cancelled) return;
            setLoading(false);
        });

        return () => { cancelled = true; };
    }, [enabled, endpoint]);

    const saveNow = useCallback(async (nextValue) => {
        if (!enabled || !endpoint) return;
        const value = typeof nextValue === 'string' ? nextValue : '';
        if (value === lastSavedValueRef.current) return;

        setSaving(true);
        setError(null);
        try {
            const trimmed =
                Number.isFinite(maxLength) && maxLength > 0
                    ? value.slice(0, maxLength)
                    : value;
            const res = await api.put(endpoint, { json: { notes: trimmed } });
            if (res.status === 200) {
                lastSavedValueRef.current = trimmed;
                setNotesUpdated(res.data?.notesUpdated ?? null);
            } else {
                setError(res.data?.message || 'Failed to save notes.');
            }
        } catch (e) {
            setError(e?.message || 'Failed to save notes.');
        } finally {
            setSaving(false);
        }
    }, [enabled, endpoint, maxLength]);

    const setNotesDebounced = useCallback((next) => {
        const value = typeof next === 'string' ? next : '';
        setNotes(value);

        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        saveTimerRef.current = setTimeout(() => {
            saveTimerRef.current = null;
            saveNow(value);
        }, 1000);
    }, [saveNow]);

    const flush = useCallback(() => {
        if (saveTimerRef.current) {
            clearTimeout(saveTimerRef.current);
            saveTimerRef.current = null;
        }
        saveNow(notes);
    }, [notes, saveNow]);

    useEffect(() => {
        return () => {
            if (saveTimerRef.current) {
                clearTimeout(saveTimerRef.current);
                saveTimerRef.current = null;
            }
        };
    }, []);

    return {
        notes,
        setNotes: setNotesDebounced,
        flush,
        loading,
        saving,
        error,
        notesUpdated,
        hasNotes: Boolean(typeof notes === 'string' && notes.trim().length > 0),
    };
}

